// Currency conversion service for Eqaya - Stablecoin focused
class CurrencyService {
  constructor() {
    // Exchange rates (in production, fetch from API)
    this.rates = {
      USD_TO_ZWL: 5800,     // 1 USD = 5,800 ZWL (parallel rate)
      GBP_TO_ZWL: 7300,     // 1 GBP = 7,300 ZWL
      EUR_TO_ZWL: 6200,     // 1 EUR = 6,200 ZWL
      ZAR_TO_ZWL: 320,      // 1 ZAR = 320 ZWL
      GBP_TO_USD: 1.26,     // 1 GBP = 1.26 USD
      EUR_TO_USD: 1.07,     // 1 EUR = 1.07 USD
      ZAR_TO_USD: 0.055,    // 1 ZAR = 0.055 USD
    };

    this.supportedCurrencies = [
      { code: 'USD', symbol: '$', name: 'US Dollar', stablecoin: 'USDC' },
      { code: 'GBP', symbol: '£', name: 'British Pound', stablecoin: 'USDC' },
      { code: 'EUR', symbol: '€', name: 'Euro', stablecoin: 'EURC' },
      { code: 'ZAR', symbol: 'R', name: 'South African Rand', stablecoin: 'USDC' },
    ];

    // Stablecoin configuration
    this.stablecoins = {
      USDC: { name: 'USD Coin', symbol: 'USDC', decimals: 6 },
      USDT: { name: 'Tether', symbol: 'USDT', decimals: 6 },
      EURC: { name: 'Euro Coin', symbol: 'EURC', decimals: 6 }
    };
  }

  // Convert any currency to USD (base for stablecoins)
  toUSD(amount, currency) {
    if (currency === 'USD') return parseFloat(amount);
    
    const rate = this.rates[`${currency}_TO_USD`];
    if (!rate) {
      // If no direct rate, calculate through ZWL
      const zwlAmount = this.toZWL(amount, currency);
      return zwlAmount / this.rates.USD_TO_ZWL;
    }
    return parseFloat(amount) * rate;
  }

  // Convert USD to any currency
  fromUSD(usdAmount, currency) {
    if (currency === 'USD') return parseFloat(usdAmount);
    
    const rate = this.rates[`${currency}_TO_USD`];
    if (!rate) {
      // If no direct rate, calculate through ZWL
      const zwlAmount = parseFloat(usdAmount) * this.rates.USD_TO_ZWL;
      return this.fromZWL(zwlAmount, currency);
    }
    return parseFloat(usdAmount) / rate;
  }

  // Format currency for display
  formatCurrency(amount, currency) {
    const cur = this.supportedCurrencies.find(c => c.code === currency);
    if (!cur) return `${amount} ${currency}`;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  // Format stablecoin amount
  formatStablecoin(amount, stablecoin = 'USDC') {
    const coin = this.stablecoins[stablecoin];
    if (!coin) return `${amount} ${stablecoin}`;
    
    return `${parseFloat(amount).toFixed(2)} ${coin.symbol}`;
  }

  // Get platform fee in currency
  getPlatformFee(amount, currency) {
    const fee = parseFloat(amount) * 0.025; // 2.5%
    return this.formatCurrency(fee, currency);
  }

  // Convert to Zimbabwe dollars
  toZWL(amount, fromCurrency) {
    const rate = this.rates[`${fromCurrency}_TO_ZWL`];
    if (!rate) {
      // If no direct rate, convert through USD
      const usdAmount = this.toUSD(amount, fromCurrency);
      return usdAmount * this.rates.USD_TO_ZWL;
    }
    return parseFloat(amount) * rate;
  }

  // Convert from Zimbabwe dollars
  fromZWL(zwlAmount, toCurrency) {
    const rate = this.rates[`${toCurrency}_TO_ZWL`];
    if (!rate) {
      // If no direct rate, convert through USD
      const usdAmount = parseFloat(zwlAmount) / this.rates.USD_TO_ZWL;
      return this.fromUSD(usdAmount, toCurrency);
    }
    return parseFloat(zwlAmount) / rate;
  }

  // Get exchange rate display
  getExchangeRateDisplay(amount, fromCurrency, toCurrency = 'ZWL') {
    const convertedAmount = toCurrency === 'ZWL' 
      ? this.toZWL(amount, fromCurrency)
      : this.toUSD(amount, fromCurrency);
    
    return {
      original: this.formatCurrency(amount, fromCurrency),
      converted: toCurrency === 'ZWL' 
        ? this.formatCurrency(convertedAmount, 'ZWL')
        : this.formatStablecoin(convertedAmount, 'USDC'),
      rate: `1 ${fromCurrency} = ${this.formatCurrency(
        toCurrency === 'ZWL' ? this.toZWL(1, fromCurrency) : this.toUSD(1, fromCurrency), 
        toCurrency === 'ZWL' ? 'ZWL' : 'USD'
      )}`
    };
  }

  // Get recommended stablecoin for currency
  getRecommendedStablecoin(currency) {
    const cur = this.supportedCurrencies.find(c => c.code === currency);
    return cur?.stablecoin || 'USDC';
  }

  // Convert to stablecoin units (with decimals)
  toStablecoinUnits(amount, currency, stablecoin = 'USDC') {
    const usdAmount = this.toUSD(amount, currency);
    const coin = this.stablecoins[stablecoin];
    if (!coin) throw new Error(`Unsupported stablecoin: ${stablecoin}`);
    
    // Convert to smallest unit (e.g., USDC uses 6 decimals)
    return Math.floor(usdAmount * Math.pow(10, coin.decimals));
  }

  // Convert from stablecoin units to currency
  fromStablecoinUnits(units, stablecoin = 'USDC', toCurrency = 'USD') {
    const coin = this.stablecoins[stablecoin];
    if (!coin) throw new Error(`Unsupported stablecoin: ${stablecoin}`);
    
    // Convert from smallest unit to USD
    const usdAmount = units / Math.pow(10, coin.decimals);
    
    // Convert to target currency
    return toCurrency === 'USD' ? usdAmount : this.fromUSD(usdAmount, toCurrency);
  }
  
  // Convert from ETH to any currency (for demo purposes)
  fromETH(ethAmount, toCurrency = 'USD') {
    // For demo purposes, we'll use a fixed ETH to USD rate of $3,000
    const ethToUsdRate = 3000;
    const usdAmount = parseFloat(ethAmount) * ethToUsdRate;
    
    // Convert to target currency
    return toCurrency === 'USD' ? usdAmount : this.fromUSD(usdAmount, toCurrency);
  }
  
  // Convert from any currency to ETH (for demo purposes)
  toETH(amount, fromCurrency = 'USD') {
    // For demo purposes, we'll use a fixed ETH to USD rate of $3,000
    const ethToUsdRate = 3000;
    const usdAmount = this.toUSD(amount, fromCurrency);
    
    // Convert USD to ETH
    return usdAmount / ethToUsdRate;
  }
}

// Create singleton instance
const currencyService = new CurrencyService();
export default currencyService;